import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface RedirectOptions {
  replace?: boolean;
  state?: Record<string, unknown>;
  exactMatch?: boolean;
  external?: boolean;
}

interface RedirectProps {
  from: string;
  to: string;
  options?: RedirectOptions;
}

/**
 * Normalizes a path by removing trailing slashes and ensuring leading slash
 */
const normalizePath = (path: string): string => {
  // Remove trailing slashes and ensure leading slash
  return '/' + path.replace(/^\/+|\/+$/g, '');
};

/**
 * Custom hook to handle redirect logic
 */
export const useRedirect = (
  from: string,
  to: string,
  options: RedirectOptions = {}
): void => {
  const navigate = useNavigate();
  const location = useLocation();
  
  React.useEffect(() => {
    const normalizedFrom = normalizePath(from);
    const normalizedCurrent = normalizePath(location.pathname);

    const exactMatch = options.exactMatch ?? true;
    const shouldRedirect = exactMatch 
      ? normalizedCurrent === normalizedFrom 
      : normalizedCurrent.startsWith(normalizedFrom);
    
    if (shouldRedirect) {
      if (options.external) {
        const shouldReplace = options.replace ?? true;
        
        if (shouldReplace) {
          window.location.replace(to);
        } else {
          window.location.href = to;
        }
      } else {
        const normalizedTo = normalizePath(to);
        const defaultOptions = {
          replace: true,
          state: { isRedirect: true }
        };
        
        const redirectOptions = { ...defaultOptions, ...options };
        
        navigate(normalizedTo, {
          replace: redirectOptions.replace,
          state: redirectOptions.state
        });
      }
    }
  }, [from, to, location.pathname, navigate, options]);
};

/**
 * Redirect component for React Router that handles client-side redirects
 * @param props - Component props
 * @returns null - Component renders nothing
 */
export const Redirect: React.FC<RedirectProps> = ({ from, to, options = {} }) => {
  useRedirect(from, to, options);
  return null;
};

export default Redirect;